import React from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { RetentionTooltip } from './RetentionTooltip';
import {
  calculateRetentionDays,
  formatRetentionText,
  formatRetentionTextShort,
} from '../utils/fileUtils';

type RetentionTier = 'permanent' | 'distant' | 'amber' | 'orange' | 'red';

const tierFromDays = (daysRemaining: number): RetentionTier => {
  if (daysRemaining === -1) return 'permanent';
  if (daysRemaining >= 7) return 'distant';
  if (daysRemaining >= 3) return 'amber';
  if (daysRemaining >= 2) return 'orange';
  return 'red'; // 0 or 1 day
};

const tierTextClass = (tier: RetentionTier): string => {
  switch (tier) {
    case 'amber': return 'text-amber-400 font-medium';
    case 'orange': return 'text-orange-400 font-semibold';
    case 'red': return 'text-red-400 font-semibold';
    default: return '';
  }
};

interface FileRetentionProps {
  uploadedAt: string | number;
  ttl?: number | null; // TTL timestamp (null/undefined = permanent file)
  variant?: 'default' | 'compact' | 'badge';
  showIcon?: boolean;
  className?: string;
}

/**
 * Reusable component for displaying file retention period with tooltip.
 * Shows "n days left" with dotted underline and precise expiry time on hover.
 * Permanent files (no TTL) render nothing — the absence of an expiration
 * chip is itself the signal that the file doesn't expire. Callers that need
 * to suppress neighbouring chrome (leading bullets, separator spans) on
 * permanent files must check `isPermanentFile(ttl)` themselves at the call
 * site, since this component will not emit any markup in that case.
 */
export const FileRetention: React.FC<FileRetentionProps> = ({
  uploadedAt,
  ttl,
  variant = 'default',
  showIcon = true,
  className = ''
}) => {
  // Hooks must run unconditionally — read reduced-motion preference before
  // any early-return so React's hook ordering stays stable across renders
  // (e.g. when a file transitions from temporary to permanent in-place).
  const reduceMotion = useReducedMotion();

  const daysRemaining = calculateRetentionDays(uploadedAt, ttl);
  const retentionText = formatRetentionText(daysRemaining);
  const isPermanent = daysRemaining === -1;

  // Permanent files: render nothing across every variant. The presence of
  // an expiration chip means the file expires; the absence means it doesn't.
  if (isPermanent) return null;

  const tier = tierFromDays(daysRemaining);
  const tierClass = tierTextClass(tier);
  const pulseProps = reduceMotion
    ? {}
    : tier === 'orange'
      ? { animate: { opacity: [1, 0.7, 1] }, transition: { duration: 2.5, repeat: Infinity } }
      : tier === 'red'
        ? { animate: { opacity: [1, 0.55, 1] }, transition: { duration: 1.5, repeat: Infinity } }
        : {};

  // Badge variant (used in table cells)
  if (variant === 'badge') {
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full bg-soundwave-500/10 whitespace-nowrap ${className}`}>
        <RetentionTooltip uploadedAt={uploadedAt} ttl={ttl}>
          {retentionText.replace(' left', '')}
        </RetentionTooltip>
      </span>
    );
  }

  // Compact variant (inline with other metadata) — emits the short "3d" /
  // "Today" grammar. Depth isn't lost: the RetentionTooltip on hover still
  // surfaces the precise expiry time (see RetentionTooltip.tsx).
  if (variant === 'compact') {
    return (
      <RetentionTooltip uploadedAt={uploadedAt} ttl={ttl} className={className}>
        <motion.span className={`inline-flex items-center ${tierClass}`} {...pulseProps}>
          {formatRetentionTextShort(daysRemaining)}
          {(tier === 'red' || tier === 'amber') && (
            <Link
              to="/profile/plan"
              onClick={(e) => e.stopPropagation()}
              className={`ml-2 hidden md:inline underline underline-offset-2 ${tier === 'red' ? 'text-red-400 hover:text-red-300' : 'text-amber-400 hover:text-amber-300'}`}
            >
              Upgrade →
            </Link>
          )}
        </motion.span>
      </RetentionTooltip>
    );
  }

  // Default variant (with icon)
  return (
    <div className={`flex items-center gap-2 ${className} ${tierClass}`}>
      {showIcon && <Clock className="w-4 h-4" />}
      <RetentionTooltip uploadedAt={uploadedAt} ttl={ttl}>
        <motion.span className="inline-flex items-center" {...pulseProps}>
          {retentionText}
        </motion.span>
      </RetentionTooltip>
      {(tier === 'red' || tier === 'amber') && (
        <Link
          to="/profile/plan"
          onClick={(e) => e.stopPropagation()}
          className={`hidden md:inline underline underline-offset-2 text-sm ${tier === 'red' ? 'text-red-400 hover:text-red-300' : 'text-amber-400 hover:text-amber-300'}`}
        >
          Upgrade →
        </Link>
      )}
    </div>
  );
};
